/** biome-ignore-all lint/suspicious/noConsole: need to log a bit in this file */
import { webSocketUrl } from '@glow/common'
import { siteMetricsCaptureErrorMessage } from '@glow/instrumentation'
import { isImmutable } from 'immutable'
import isNil from 'lodash-es/isNil'
import { Store } from 'redux'
import { withBase } from '../utils/appBase'
import { WebSocketContextImmutable } from './wsUtils'

let source: WebSocket | null
let currentContext: WebSocketContextImmutable | null = null
let keepOpen = true
let lastPing: Date
let retrying = false
let checkerHandle: ReturnType<typeof setInterval> | null = null
let retries = 0
const retriesUntilNotification = 3
const seenSentryMessages: { [k: string]: boolean } = {}

const wsCodeLogin = 3001
const wsCodeReconnect = 3002
const wsCodeNoPing = 3004
const wsCodeClose = 3003

interface Handlers {
  errorHandler: (error: string) => void
  messageHandler: (message: string) => void
  clearErrorHandler: () => void
}

const retryOpening = (handlers: Handlers) => {
  if (!keepOpen || retrying) {
    return
  }
  retrying = true
  retries++
  const wsOpeningTimeout = 2000
  setTimeout(() => {
    console.log(`ws closed, reopening ${keepOpen}`)
    if (keepOpen) {
      retrying = false
      startWS(null, handlers)
    }
  }, wsOpeningTimeout)
}

// biome-ignore lint/suspicious/noExplicitAny: Do not currently have types for WebSocket events
function logAndSendToSentry(localSource: WebSocket, code: number, reason: string, event: any = null) {
  const url = localSource?.url || ''
  const message = `doRetryFromFailure [${code}][${reason}][${url}]`
  const eventCode = event?.code
  const eventReason = event?.reason
  const scope = { url, code, reason, eventCode, eventReason }

  console.log(message, scope, event)
  sendToSentryIfUnique(message, scope)
}

// Only send unique messages to avoid flooding Sentry with reconnect errors.
function sendToSentryIfUnique(message: string, scope: {}) {
  const key = message + JSON.stringify(scope)
  if (!seenSentryMessages[key]) {
    seenSentryMessages[key] = true

    try {
      console.log('sendToSentryIfUnique', 'send', message, scope)
      siteMetricsCaptureErrorMessage(message, scope)
    } catch (err) {
      console.error(err)
    }
  }
}

const doRetryFromFailure = (localSource: WebSocket, code: number, reason: string, handlers: Handlers) => {
  localSource?.close(code, reason)
  source = null
  retryOpening(handlers)
}

const wsCheckInterval = 10000
const pingTimeoutSeconds = 30

// The checker outlives individual sockets: it always inspects the current
// `source`, so a socket replaced by a retry is still covered.
const setupWsChecker = (handlers: Handlers) => {
  if (checkerHandle !== null) return

  checkerHandle = setInterval(() => {
    const localSource = source
    if (!(keepOpen && localSource)) return

    const staleBefore = new Date()
    staleBefore.setSeconds(staleBefore.getSeconds() - pingTimeoutSeconds)
    if (lastPing && staleBefore > lastPing) {
      const showNotification = retries > retriesUntilNotification
      const reason = `no ping in ${pingTimeoutSeconds} seconds`
      doRetryFromFailure(localSource, wsCodeNoPing, reason, handlers)
      if (showNotification) {
        handlers.errorHandler('notifications.networkError')
        logAndSendToSentry(localSource, wsCodeNoPing, reason)
      }
    } else {
      console.log('ping pong ok!')
    }
  }, wsCheckInterval)
}

const stopWsChecker = () => {
  if (checkerHandle === null) return
  clearInterval(checkerHandle)
  checkerHandle = null
}

export const startWS = (store: Store | null, handlers: Handlers) => {
  keepOpen = true
  if (isNil(source)) {
    const context = store?.getState()?.get('context')
    currentContext = context ? context : currentContext
    console.debug(`starting WS with url ${webSocketUrl()}`, source, currentContext?.toJS())

    const localSource = new WebSocket(webSocketUrl())

    source = localSource
    // Give the new socket a full ping window before the checker judges it.
    lastPing = new Date()

    setupWsChecker(handlers)

    localSource.onmessage = (e) => {
      const data = e.data
      if (data === 'login') {
        console.error('WS told me to log in')
        localSource.close(wsCodeLogin, 'login')
        window.location.href = withBase('/')
      } else if (data === 'ping') {
        lastPing = new Date()
        localSource.send('pong')
      } else if (data === 'ok') {
        lastPing = new Date()
      } else {
        handlers.messageHandler(JSON.parse(e.data))
      }
    }
    localSource.onerror = (e) => {
      console.error('WS got error', retries, e)
      const showNotification = retries > retriesUntilNotification
      const reason = 'error'
      if (showNotification) {
        handlers.errorHandler('notifications.networkError')
        logAndSendToSentry(localSource, wsCodeReconnect, reason, e)
      }
      doRetryFromFailure(localSource, wsCodeReconnect, reason, handlers)
    }
    localSource.onopen = (e) => {
      console.log('WS opened', e)
      try {
        const localContext = store?.getState()?.get('context')
        const newContext = localContext ? localContext : currentContext
        if (isImmutable(newContext)) source?.send(JSON.stringify(newContext.toJS()))
      } catch (e) {
        console.error('could not send WS message...', e)
      }
      retries = 0
    }
    localSource.onclose = (e) => {
      console.log('I got a close', e)
      const showNotification = retries > retriesUntilNotification
      const reason = 'close'
      if (showNotification) {
        handlers.errorHandler('notifications.networkError')
        logAndSendToSentry(localSource, wsCodeClose, reason, e)
      }
      doRetryFromFailure(localSource, wsCodeClose, reason, handlers)
    }
  }
}

export const changeWSContext = (newContext: WebSocketContextImmutable) => {
  const oldContext = currentContext
  currentContext = newContext

  if (JSON.stringify(oldContext?.toJS()) !== JSON.stringify(newContext?.toJS())) {
    if (!isNil(source) && newContext && source.readyState === WebSocket.OPEN) {
      try {
        console.log('changing ws context to:', newContext?.toJS())
        source.send(JSON.stringify(newContext.toJS()))
      } catch (e) {
        console.error('failed to send message on websocket', e)
      }
    } else console.log('will not try to change')
  }
}

export const stopWS = () => {
  console.debug('stopping ws')
  stopWsChecker()
  keepOpen = false
  source?.close()
  source = null
}
